import { DialogComponent } from '@theme/dialog';
import { scrollIntoView } from '@theme/scrolling';

const SLIDE_SELECTOR = 'slideshow-slide.resource-list__slide';
const PLAYABLE_MEDIA_SELECTOR = 'video, iframe, deferred-media, model-viewer, product-model, video-background';
const INTERACTIVE_SELECTOR = 'a[href], button, input, label, select, summary, textarea';

/** Pointer travel, in pixels, above which a click is treated as the end of a swipe. */
const DRAG_THRESHOLD = 10;

/**
 * Opens carousel slide images full screen in a dialog with its own slideshow.
 *
 * The viewer is built from the images already rendered in the carousel, so it works with any
 * theme block a merchant puts inside a slide. Slides holding playable media are left alone.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The full screen dialog.
 * @property {HTMLTemplateElement} template - The inert markup of the viewer slideshow.
 *
 * @extends DialogComponent
 */
export class CarouselLightbox extends DialogComponent {
  requiredRefs = ['dialog', 'template'];

  /** @type {import('./slideshow').Slideshow | null} */
  #viewer = null;

  /** @type {{ x: number, y: number, target: EventTarget | null } | null} */
  #press = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('pointercancel', this.#handlePointerCancel);
    this.addEventListener('click', this.#handleTriggerClick);
    this.addEventListener('keydown', this.#handleTriggerKeyDown);

    this.#markTriggers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('pointercancel', this.#handlePointerCancel);
    this.removeEventListener('click', this.#handleTriggerClick);
    this.removeEventListener('keydown', this.#handleTriggerKeyDown);
  }

  /**
   * Opens the dialog on a given image.
   *
   * @param {number} index - The index of the image within the zoomable slides.
   */
  openAtIndex(index) {
    this.showDialog();
    requestAnimationFrame(() => {
      this.#viewer ??= this.#createViewer(index);
      this.#showSlide(index);
    });
  }

  /**
   * Moves the viewer to a given image.
   *
   * Slideshow.select() is a no-op when the requested slide is already the current one, which is
   * the case right after the viewer is built, and a closed dialog resets the scroller behind the
   * slideshow's back. So the scroll position is set here on every open.
   *
   * @param {number} index - The index of the image to show.
   */
  #showSlide(index) {
    const viewer = this.#viewer;
    const slide = viewer?.refs.slides?.[index];

    if (!viewer || !slide) return;

    viewer.current = index;

    scrollIntoView(slide, { ancestor: viewer.refs.scroller, behavior: 'instant', inline: 'start' });
  }

  /**
   * The carousel slides that can be zoomed, in DOM order.
   *
   * @returns {Element[]} The zoomable slides.
   */
  get #triggers() {
    const carousel = this.querySelector(':scope > .resource-list');

    if (!carousel) return [];

    return Array.from(carousel.querySelectorAll(SLIDE_SELECTOR)).filter(
      (slide) => slide.querySelector('img') && !slide.querySelector(PLAYABLE_MEDIA_SELECTOR),
    );
  }

  /**
   * Flags the zoomable slides so they read and behave as controls.
   */
  #markTriggers() {
    const label = this.dataset.zoomLabel;

    for (const slide of this.#triggers) {
      slide.classList.add('carousel-lightbox__trigger');

      // Slides with their own controls keep their semantics; only the surrounding image zooms.
      if (slide.querySelector(INTERACTIVE_SELECTOR)) continue;

      slide.setAttribute('role', 'button');
      slide.setAttribute('tabindex', '0');

      if (label) slide.setAttribute('aria-label', label);
    }
  }

  /**
   * Resolves the zoomable index of the slide an event happened in.
   *
   * @param {EventTarget | null} target - The event target.
   * @returns {number} The index, or -1 when the target is not part of a zoomable slide.
   */
  #indexOf(target) {
    if (!(target instanceof Element)) return -1;

    const slide = target.closest(SLIDE_SELECTOR);

    if (!slide) return -1;

    return this.#triggers.indexOf(slide);
  }

  /**
   * @param {PointerEvent} event - The pointerdown event.
   */
  #handlePointerDown = (event) => {
    this.#press = { x: event.clientX, y: event.clientY, target: event.target };
  };

  #handlePointerCancel = () => {
    this.#press = null;
  };

  /**
   * @param {MouseEvent} event - The click event.
   */
  #handleTriggerClick = (event) => {
    const press = this.#press;

    this.#press = null;

    if (this.refs.dialog.open) return;
    if (press && Math.hypot(event.clientX - press.x, event.clientY - press.y) > DRAG_THRESHOLD) return;

    const target = press?.target ?? event.target;
    const index = this.#indexOf(target);

    if (index === -1) return;
    if (target instanceof Element && target.closest(INTERACTIVE_SELECTOR)) return;

    event.preventDefault();
    this.openAtIndex(index);
  };

  /**
   * @param {KeyboardEvent} event - The keydown event.
   */
  #handleTriggerKeyDown = (event) => {
    if (this.refs.dialog.open) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!(event.target instanceof Element) || event.target.getAttribute('role') !== 'button') return;

    const index = this.#indexOf(event.target);

    if (index === -1) return;

    event.preventDefault();
    this.openAtIndex(index);
  };

  /**
   * Builds the viewer slideshow and connects it to the open dialog.
   *
   * @param {number} index - The index of the image to show first.
   * @returns {import('./slideshow').Slideshow | null} The viewer slideshow.
   */
  #createViewer(index) {
    const { dialog, template } = this.refs;
    const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    const slideshow = fragment.querySelector('slideshow-component');
    const scroller = fragment.querySelector('[ref="scroller"]');

    if (!slideshow || !scroller) return null;

    this.#sourceImages().forEach((image, imageIndex) =>
      scroller.append(createSlide(image, imageIndex, imageIndex === index)),
    );

    // Without this the slideshow scrolls itself back to the first slide once it initialises.
    slideshow.setAttribute('initial-slide', String(index));

    // The slideshow only reads its slides when it connects, so fill the fragment first.
    dialog.append(fragment);

    return /** @type {any} */ (slideshow);
  }

  /**
   * The carousel image of each zoomable slide, in the same order as the triggers.
   *
   * @returns {HTMLImageElement[]} The source images.
   */
  #sourceImages() {
    return this.#triggers.map((slide) => {
      const images = /** @type {HTMLImageElement[]} */ (Array.from(slide.querySelectorAll('img')));

      // Cards can hold a desktop and a mobile image; only one of them is rendered at a time.
      return images.find((image) => image.offsetParent !== null) ?? images[0];
    });
  }
}

/**
 * Creates a viewer slide showing an image at its natural proportions.
 *
 * @param {HTMLImageElement} image - The image rendered in the carousel.
 * @param {number} index - The index of the slide.
 * @param {boolean} immediate - Whether the slide is the one the dialog opens on.
 * @returns {HTMLElement} The viewer slide.
 */
function createSlide(image, index, immediate) {
  const slide = document.createElement('slideshow-slide');

  slide.className = 'carousel-lightbox__slide';
  slide.setAttribute('ref', 'slides[]');
  slide.setAttribute('slide-id', `carousel-lightbox-${index}`);
  slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');

  const zoomed = document.createElement('img');

  zoomed.className = 'carousel-lightbox__image';
  zoomed.src = image.src;
  zoomed.alt = image.alt;
  zoomed.sizes = '100vw';
  zoomed.loading = immediate ? 'eager' : 'lazy';
  zoomed.fetchPriority = immediate ? 'high' : 'auto';
  zoomed.decoding = 'async';

  if (image.srcset) zoomed.srcset = image.srcset;

  // Carry the intrinsic size over so the slide reserves the right shape before the image decodes.
  for (const attribute of ['width', 'height']) {
    const value = image.getAttribute(attribute);

    if (value) zoomed.setAttribute(attribute, value);
  }

  slide.append(zoomed);

  return slide;
}

if (!customElements.get('carousel-lightbox')) {
  customElements.define('carousel-lightbox', CarouselLightbox);
}
